import { Color } from '@persona-id/design-tokens';
import { VFC } from 'react';
import styled from 'styled-components';

import { StepFlowVerticalLine, StepPosition } from 'dashboard/components/StepFlow/StepFlowElements';

const CurveContainer = styled('div')<{ width: number; direction: 'out' | 'in' }>`
  width: ${({ width }) => width}px;
  display: flex;
  justify-content: center;
  ${({ direction }) => (direction === 'out' ? 'margin-top: -2px;' : 'margin-bottom: -2px;')}
`;

const WorkflowCurverContainer = styled('div')<{ width: number }>`
  width: ${({ width }) => width}px;
  display: flex;
  justify-content: center;
`;

const WorkflowCurveSvg = styled('svg')<{ rotationDegrees: number; direction?: 'left' | 'right' }>`
  transform: rotate(${({ rotationDegrees }) => rotationDegrees}deg);
  margin-left: ${({ direction }) => (direction === 'left' ? '15px' : '-15px')};
`;

interface BranchCurveProps {
  curveDirection: 'left' | 'right';
  branchDirection: 'in' | 'out';
  width: number;
  leftPathColor?: string;
  rightPathColor?: string;
}

const BranchCurve: VFC<BranchCurveProps> = ({
  curveDirection,
  branchDirection,
  width,
  leftPathColor,
  rightPathColor,
}) => {
  let rotationDegrees;
  let color;
  if (curveDirection === 'left') {
    rotationDegrees = branchDirection === 'out' ? 270 : 180;
    color = leftPathColor;
  } else {
    rotationDegrees = branchDirection === 'out' ? 0 : 90;
    color = rightPathColor;
  }

  return (
    <WorkflowCurverContainer width={width}>
      <WorkflowCurveSvg
        xmlns='http://www.w3.org/2000/svg'
        width='17'
        height='17'
        viewBox='0 0 17 17'
        fill='none'
        rotationDegrees={rotationDegrees}
        direction={curveDirection}
      >
        <path
          d='M16 17C16 8.16344 8.83656 1 0 1'
          stroke={color || Color.CorePurple200}
          strokeWidth='2'
        />
      </WorkflowCurveSvg>
    </WorkflowCurverContainer>
  );
};

interface StepFlowBranchCurvesProps {
  branchDirection: 'in' | 'out';
  childTokens: string[];
  stepTokenToPositionMap: Map<string, StepPosition>;
  width: number;
  leftPathColor?: string;
  rightPathColor?: string;
  pathToChildIndex?: number;
}

export const StepFlowBranchCurvesContainer = styled('div')`
  display: flex;
  justify-content: center;
`;

const StepFlowBranchCurves: VFC<StepFlowBranchCurvesProps> = ({
  branchDirection,
  childTokens,
  stepTokenToPositionMap,
  width,
  leftPathColor,
  rightPathColor,
  pathToChildIndex,
}) => {
  const drawPath = pathToChildIndex != null && pathToChildIndex >= 0;
  return (
    <StepFlowBranchCurvesContainer>
      {childTokens.map((token, i) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const branchWidth = stepTokenToPositionMap.get(token).width;
        const hasPath = drawPath && i === pathToChildIndex;

        if (childTokens.length === 1) {
          return (
            <CurveContainer key={token} width={branchWidth} direction={branchDirection}>
              <StepFlowVerticalLine
                width={branchWidth}
                color={hasPath ? Color.CorePurple600 : leftPathColor || rightPathColor}
              />
            </CurveContainer>
          );
        }

        // Note: because the tree is unbalanced, we are doing something simple and drawing straight lines for all interior nodes
        //       otherwise we ran into issues with the curves being underneath the parent and looking awkward
        // TODO: The case where all children end up to one side of the parent node
        if (i === 0) {
          return (
            <CurveContainer key={token} width={branchWidth} direction={branchDirection}>
              <BranchCurve
                curveDirection='left'
                branchDirection={branchDirection}
                width={width}
                leftPathColor={hasPath ? Color.CorePurple600 : leftPathColor}
                rightPathColor={hasPath ? Color.CorePurple600 : rightPathColor}
              />
            </CurveContainer>
          );
        } else if (i === childTokens.length - 1) {
          return (
            <CurveContainer key={token} width={branchWidth} direction={branchDirection}>
              <BranchCurve
                curveDirection='right'
                branchDirection={branchDirection}
                width={width}
                leftPathColor={hasPath ? Color.CorePurple600 : leftPathColor}
                rightPathColor={hasPath ? Color.CorePurple600 : rightPathColor}
              />
            </CurveContainer>
          );
        } else {
          return (
            <CurveContainer key={token} width={branchWidth} direction={branchDirection}>
              <StepFlowVerticalLine
                width={branchWidth}
                color={hasPath ? Color.CorePurple600 : leftPathColor || rightPathColor}
              />
            </CurveContainer>
          );
        }
      })}
    </StepFlowBranchCurvesContainer>
  );
};

export default StepFlowBranchCurves;
